// src/components/admin/CurveEditor.js
import React, { useState, useEffect } from 'react';
import CurveChart from './CurveChart';
import CurveControls from './CurveControls';
import CurveSmoothingControls from './CurveSmoothingControls';
import { INITIAL_CURVE_DATA } from '../../../data/initialCurveData';
import { generateDensePoints, calculateSamplePrice } from './curveUtils';

// Helper functions - keep for fallback
const getCurveData = (jobType, customerTier = 'retail') => {
  return INITIAL_CURVE_DATA[jobType]?.[customerTier] || null;
};

const CurveEditor = () => {
  // State management
  const [selectedJobType, setSelectedJobType] = useState('letterheads'); // Keep default for initial render
  const [customerTier, setCustomerTier] = useState('retail');
  const [controlPoints, setControlPoints] = useState([]);
  const [isLogScale, setIsLogScale] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [sampleQuantity, setSampleQuantity] = useState(100);
  
  // NEW: Dynamic job types state
  const [jobTypes, setJobTypes] = useState(['letterheads']); // Fallback default
  const [jobTypesLoading, setJobTypesLoading] = useState(true);

  // API URL
  const API_URL = process.env.REACT_APP_API_URL || '';

  // NEW: Load available job types from API
  useEffect(() => {
    const fetchJobTypes = async () => {
      setJobTypesLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/small-format/curves/job-types`, {
          credentials: 'include',
        });
        if (response.ok) {
          const types = await response.json();
          console.log('Loaded job types from API:', types);
          
          if (types.length > 0) {
            setJobTypes(types);
            // If current selectedJobType is not in the new list, switch to the first available
            if (!types.includes(selectedJobType)) {
              setSelectedJobType(types[0]);
            }
          } else {
            // Fallback to hardcoded if no curves in database
            console.log('No job types found in database, using fallback');
            const fallbackTypes = Object.keys(INITIAL_CURVE_DATA);
            setJobTypes(fallbackTypes);
          }
        } else {
          console.error('Failed to load job types from API');
          // Fallback to hardcoded
          const fallbackTypes = Object.keys(INITIAL_CURVE_DATA);
          setJobTypes(fallbackTypes);
        }
      } catch (error) {
        console.error('Error loading job types:', error);
        // Fallback to hardcoded
        const fallbackTypes = Object.keys(INITIAL_CURVE_DATA);
        setJobTypes(fallbackTypes);
      } finally {
        setJobTypesLoading(false);
      }
    };

    fetchJobTypes();
  }, [API_URL]); // Only depend on API_URL

  // Load initial curve data - MODIFIED to wait for job types
  useEffect(() => {
    if (jobTypesLoading) return; // Wait for job types to load first
    
    const loadCurveData = async () => {
      // First try to load from API
      try {
        const response = await fetch(`${API_URL}/api/small-format/curves/${selectedJobType}/${customerTier}`, {
          credentials: 'include',
        });
        if (response.ok) {
          const apiData = await response.json();
          console.log('Loaded curve from API:', apiData);
          setControlPoints(apiData.control_points || []);
        } else {
          console.log('No curve found in API, trying fallback data');
          // Fallback to hardcoded data
          const curveData = getCurveData(selectedJobType, customerTier);
          if (curveData) {
            setControlPoints([...curveData.controlPoints]);
          } else {
            console.log('No fallback data available');
            setControlPoints([]);
          }
        }
      } catch (error) {
        console.error('Error loading curve from API, using local fallback:', error);
        const curveData = getCurveData(selectedJobType, customerTier);
        if (curveData) {
          setControlPoints([...curveData.controlPoints]);
        } else {
          setControlPoints([]);
        }
      } finally {
        setHasUnsavedChanges(false);
      }
    };

    loadCurveData();
  }, [selectedJobType, customerTier, jobTypesLoading, API_URL]);

  // Handle control point updates
  const handleControlPointsChange = (newPoints) => {
    setControlPoints(newPoints);
    setHasUnsavedChanges(true);
  };

  // Generate dense points for backend consumption
  const getDensePoints = () => {
    return generateDensePoints(controlPoints, {
      sampleRate: 25, // Every 25 quantities
      smoothing: 'spline'
    });
  };

  // Calculate sample pricing
  const getSamplePrice = () => {
    if (controlPoints.length < 2) return null;
    
    return calculateSamplePrice(sampleQuantity, controlPoints, {
      basePrice: 10, // Mock base price for demonstration
      jobType: selectedJobType
    });
  };

  // Save curve data
  const handleSave = async () => {
    if (controlPoints.length < 2) {
      alert('Please add at least 2 control points before saving.');
      return;
    }

    const payload = {
      job_type: selectedJobType,
      customer_tier: customerTier,
      controlPoints: controlPoints,
    };

    try {
      const response = await fetch(`${API_URL}/api/small-format/curves`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setHasUnsavedChanges(false);
        alert('Curve saved successfully to database!');
        
        // Refresh job types in case this was a new job type
        const typesResponse = await fetch(`${API_URL}/api/small-format/curves/job-types`, {
          credentials: 'include',
        });
        if (typesResponse.ok) {
          const updatedTypes = await typesResponse.json();
          setJobTypes(updatedTypes);
        }
      } else {
        const errorData = await response.json();
        alert(`Failed to save curve: ${errorData.error || response.statusText}`);
      }
    } catch (error) {
      console.error('Error saving curve:', error);
      alert(`Error saving curve: ${error.message}`);
    }
  };

  // Load saved curve data
  const handleLoad = async () => {
    try {
      const response = await fetch(`${API_URL}/api/small-format/curves/${selectedJobType}/${customerTier}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const curveData = await response.json();
        setControlPoints(curveData.control_points);
        setHasUnsavedChanges(false);
        alert('Curve loaded successfully from database!');
      } else {
        alert('No saved curve found in database for this job type/tier. Try resetting to original.');
      }
    } catch (error) {
      console.error('Error loading curve:', error);
      alert(`Error loading curve: ${error.message}`);
    }
  };

  // Reset to original data
  const handleReset = () => {
    if (window.confirm('Reset to original curve? This will lose any unsaved changes.')) {
      const originalData = getCurveData(selectedJobType, customerTier);
      if (originalData) {
        setControlPoints([...originalData.controlPoints]);
        setHasUnsavedChanges(false);
      } else {
        // If no original data, create a basic curve
        setControlPoints([
          { quantity: 1, margin: 200 },
          { quantity: 50, margin: 150 },
          { quantity: 250, margin: 100 },
          { quantity: 1000, margin: 80 }
        ]);
        setHasUnsavedChanges(true);
      }
    }
  };

  // Export curve data for backend
  const handleExport = () => {
    const exportData = {
      jobType: selectedJobType,
      customerTier: customerTier,
      controlPoints: controlPoints,
      generatedPoints: getDensePoints(),
      metadata: {
        exportedAt: new Date().toISOString(),
        pointCount: controlPoints.length,
        quantityRange: controlPoints.length > 0 ? {
          min: Math.min(...controlPoints.map(p => p.quantity)),
          max: Math.max(...controlPoints.map(p => p.quantity))
        } : { min: 0, max: 0 },
        marginRange: controlPoints.length > 0 ? {
          min: Math.min(...controlPoints.map(p => p.margin)),
          max: Math.max(...controlPoints.map(p => p.margin))
        } : { min: 0, max: 0 }
      }
    };

    // Download as JSON
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `curve_${selectedJobType}_${customerTier}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const samplePrice = getSamplePrice();
  const currentMargin = controlPoints.length > 0 ? 
    generateDensePoints(controlPoints).find(p => p.quantity >= sampleQuantity)?.margin || 0 : 0;

  // Show loading state while job types are loading
  if (jobTypesLoading) {
    return (
      <div className="max-w-7xl mx-auto px-6 pt-2 pb-6">
        <div className="text-center py-8">
          <div className="text-lg">Loading pricing curves...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 pt-2 pb-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Pricing Curve Editor</h1>
        <p className="text-gray-600">
          Adjust quantity-based margin curves for different job types. 
          Click the "Add Points" button to interactively add control points, or drag existing points to modify the curve shape.
        </p>
        {jobTypes.length > 0 && (
          <p className="text-sm text-blue-600 mt-1">
            Found {jobTypes.length} job type{jobTypes.length !== 1 ? 's' : ''} with curves: {jobTypes.join(', ')}
          </p>
        )}
      </div>

      {/* Controls */}
      <CurveControls
        selectedJobType={selectedJobType}
        setSelectedJobType={setSelectedJobType}
        customerTier={customerTier}
        setCustomerTier={setCustomerTier}
        jobTypes={jobTypes}
        isLogScale={isLogScale}
        setIsLogScale={setIsLogScale}
        hasUnsavedChanges={hasUnsavedChanges}
        onSave={handleSave}
        onLoad={handleLoad}
        onReset={handleReset}
        onExport={handleExport}
        sampleQuantity={sampleQuantity}
        setSampleQuantity={setSampleQuantity}
        controlPoints={controlPoints}
        onControlPointsChange={handleControlPointsChange}
      />

      {/* Chart */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        {controlPoints.length > 0 ? (
          <CurveChart 
            controlPoints={controlPoints}
            onControlPointsChange={handleControlPointsChange}
            isLogScale={isLogScale}
            jobType={selectedJobType}
          />
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p>No curve data available for {selectedJobType}/{customerTier}</p>
            <p className="text-sm mt-2">Click "Reset to Original" to create a basic curve, or load from another job type</p>
          </div>
        )}
      </div>

      {/* Smoothing Controls */}
      {controlPoints.length > 0 && (
        <CurveSmoothingControls
          controlPoints={controlPoints}
          onControlPointsChange={handleControlPointsChange}
          jobType={selectedJobType}
        />
      )}

      {/* Sample Pricing Preview */}
      {samplePrice && (
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <h3 className="font-semibold text-blue-900 mb-2">Live Pricing Preview</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium">Sample Quantity:</span> {sampleQuantity}
            </div>
            <div>
              <span className="font-medium">Margin at Quantity:</span> {currentMargin.toFixed(1)}%
            </div>
            <div>
              <span className="font-medium">Sample Price:</span> £{samplePrice.toFixed(2)}
            </div>
          </div>
        </div>
      )}

      {/* Curve Data Summary */}
      <div className="mt-6 bg-gray-50 rounded-lg p-4">
        <h3 className="font-semibold text-gray-900 mb-2">Curve Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="font-medium">Control Points:</span> {controlPoints.length}
          </div>
          <div>
            <span className="font-medium">Generated Points:</span> {getDensePoints().length}
          </div>
          <div>
            <span className="font-medium">Quantity Range:</span> 
            {controlPoints.length > 0 ? 
              ` ${Math.min(...controlPoints.map(p => p.quantity))} - ${Math.max(...controlPoints.map(p => p.quantity))}` : 
              ' N/A'}
          </div>
          <div>
            <span className="font-medium">Margin Range:</span> 
            {controlPoints.length > 0 ? 
              ` ${Math.min(...controlPoints.map(p => p.margin)).toFixed(1)}% - ${Math.max(...controlPoints.map(p => p.margin)).toFixed(1)}%` : 
              ' N/A'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurveEditor;